import { useState } from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../theme/tokens';

/**
 * LocaleSwitcher — selector minimalista de idioma. Estilo mono uppercase matching el
 * resto de la UI ("Powered by Scout" strip).
 *
 * Trigger: <button> con código del locale actual + chevron. Menu MUI con las 3 opciones.
 * Cuando se elige uno nuevo, react-i18next persiste el cambio en localStorage (`cp_locale`)
 * vía el LanguageDetector configurado en src/i18n/index.ts.
 *
 * El http client lee `i18n.language` antes de cada request y lo envía como `Accept-Language`
 * header — así el backend (`UseRequestLocalization`) recibe el mismo locale para sus emails
 * (magic link, digest, certificate ready, etc).
 */
const SUPPORTED = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt-BR', label: 'Português' },
] as const;

type Variant = 'topstrip' | 'sidebar';

export function LocaleSwitcher({ variant = 'topstrip' }: { variant?: Variant } = {}) {
  const { i18n } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const current = SUPPORTED.find((l) => i18n.language === l.code)
    ?? SUPPORTED.find((l) => i18n.language?.startsWith(l.code))
    ?? SUPPORTED[0];

  function open(event: React.MouseEvent<HTMLElement>) {
    setAnchor(event.currentTarget);
  }
  function close() {
    setAnchor(null);
  }
  async function select(code: string) {
    await i18n.changeLanguage(code);
    close();
  }

  const isSidebar = variant === 'sidebar';

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={open}
        data-testid="locale-switcher"
        aria-label="Change language"
        sx={{
          background: 'transparent',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontFamily: tokens.font.mono,
          fontSize: isSidebar ? 11 : 10.5,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: isSidebar ? tokens.color.muted : tokens.color.muted2,
          fontWeight: 500,
          '&:hover': { color: tokens.color.ink },
          '&:focus-visible': { outline: `2px solid ${tokens.color.accent}`, outlineOffset: 2 },
        }}
      >
        {localeShortCode(current.code)}
        <svg viewBox="0 0 12 12" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: '10px',
              border: `1px solid ${tokens.color.line}`,
              boxShadow: '0 8px 24px rgba(15,15,15,.08)',
              minWidth: 160,
            },
          },
        }}
      >
        {SUPPORTED.map((locale) => (
          <MenuItem
            key={locale.code}
            data-testid={`locale-option-${locale.code}`}
            selected={locale.code === current.code}
            onClick={() => void select(locale.code)}
            sx={{
              fontSize: 13,
              fontWeight: locale.code === current.code ? 600 : 500,
              '&.Mui-selected': { background: tokens.color.surface2 },
              '&.Mui-selected:hover': { background: tokens.color.surface2 },
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: tokens.font.mono,
                fontSize: 10,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: tokens.color.muted,
                width: 32,
              }}
            >
              {localeShortCode(locale.code)}
            </Box>
            {locale.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

/**
 * Para el botón / MenuItem prefix usamos un código corto de 2 letras:
 *   en → EN, es → ES, pt-BR → PT
 */
function localeShortCode(code: string): string {
  if (code === 'pt-BR') return 'PT';
  return code.slice(0, 2).toUpperCase();
}
